import styled, { css, keyframes } from 'styled-components';
import { colors, shadows } from '../../styles/GlobalStyles';
import { rv, containerWidth, mediaQueries } from '../../styles/responsive';

const floatUp = keyframes`
  0% { transform: translateY(30px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

export const Container = styled.div`
  width: ${containerWidth(1280, 24)};
  margin: 0 auto;
  padding: 0 ${rv(24, 20)};
`;

export const Section = styled.section<{ $bgColor?: string }>`
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: ${({ $bgColor }) => $bgColor || colors.white};
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${rv(64, 32)};
`;

export const SectionLabel = styled.span`
  color: ${colors.brand[900]};
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: ${rv(14, 12)};
  margin-bottom: ${rv(12, 8)};
  display: block;
`;

export const SectionTitle = styled.h2`
  font-size: ${rv(48, 28)};
  font-weight: 800;
  color: ${colors.slate[900]};
  margin-bottom: ${rv(20, 12)};
  letter-spacing: -0.025em;
`;

export const SectionDescription = styled.p`
  font-size: ${rv(18, 14)};
  color: ${colors.slate[500]};
  max-width: ${containerWidth(672, 24)};
  margin: 0 auto;
  line-height: 1.75;
`;

export const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rv(14, 12)} ${rv(32, 24)};
  font-weight: 600;
  font-size: ${rv(14, 13)};
  border-radius: 9999px;
  transition: all 0.2s ease;
  transform: translateY(0);

  ${({ $variant }) =>
    $variant === 'secondary'
      ? css`
          background-color: ${colors.white};
          color: ${colors.brand[900]};
          border: 1px solid ${colors.slate[200]};
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

          &:hover {
            background-color: ${colors.slate[50]};
            transform: translateY(-2px);
          }
        `
      : css`
          background-color: ${colors.brand[900]};
          color: ${colors.white};
          box-shadow: 0 10px 20px -10px rgba(0, 0, 0, 0.3);

          &:hover {
            background-color: ${colors.slate[800]};
            box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.4);
            transform: translateY(-2px);
          }
        `}
`;

export const GlassPanel = styled.div`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: ${shadows.bento};
`;

export const BentoCard = styled.div<{ $span?: number }>`
  background: ${colors.white};
  border-radius: ${rv(32, 24)};
  border: 1px solid ${colors.slate[100]};
  box-shadow: ${shadows.bento};
  padding: ${rv(32, 24)} ${rv(40, 24)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease;
  cursor: default;

  ${({ $span }) =>
    $span &&
    css`
      ${mediaQueries.tabletUp} {
        grid-column: span ${$span};
      }
    `}

  &:hover {
    box-shadow: ${shadows.bentoHover};
  }
`;

export const IconBox = styled.div<{ $bgColor?: string; $iconColor?: string }>`
  width: ${rv(56, 48)};
  height: ${rv(56, 48)};
  border-radius: ${rv(16, 12)};
  background-color: ${({ $bgColor }) => $bgColor || colors.brand[50]};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${rv(32, 20)};
  transition: transform 0.3s ease;

  svg {
    width: ${rv(24, 20)};
    height: ${rv(24, 20)};
    color: ${({ $iconColor }) => $iconColor || colors.accent.indigo};
  }

  ${BentoCard}:hover & {
    transform: scale(1.05);
  }
`;

export const AnimatedElement = styled.div<{ $delay?: number }>`
  opacity: 0;
  animation: ${floatUp} 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;
`;

export const Tag = styled.span<{ $bgColor?: string; $textColor?: string }>`
  padding: ${rv(6, 4)} ${rv(12, 10)};
  border-radius: 9999px;
  font-size: ${rv(12, 10)};
  font-weight: 600;
  letter-spacing: 0.025em;
  background-color: ${({ $bgColor }) => $bgColor || colors.slate[100]};
  color: ${({ $textColor }) => $textColor || colors.slate[600]};
`;

export const RadialGlowBg = styled.div`
  background: radial-gradient(circle at 50% 0%, #eef2ff 0%, #f5f3ff 35%, #ffffff 70%);
`;
